import type { FeatureFlagKey } from '~~/layers/base/constants/featureFlag'

/** The subset of a route `feature-enabled` needs — keeps this pure and unit-testable. */
export type FeatureFlagRoute = {
    meta: {
        featureFlag?: FeatureFlagKey
        featureFlagByParam?: {
            param: string
            map: Partial<Record<string, FeatureFlagKey>>
        }
    }
    params: Record<string, string | string[]>
}

/**
 * Resolves which feature flag governs a navigation, for `middleware/feature-enabled.ts`.
 *
 * Most pages belong to exactly one feature and use the static `featureFlag` meta key. The designer
 * routes do not: `/designer/{start,personalize}/:type/...` serves BOTH the ecommerce purchase flow
 * and the handwritten-note flow, discriminated by `:type`. Gating the whole path on a single flag
 * would break the other flow — the route-level twin of the shared-checkout-endpoint caveat in the
 * SPEC. `featureFlagByParam` maps a param value to the flag that governs it.
 *
 * A param value absent from the map is **deliberately not gated**. That is what lets the
 * `handwriting` half ship while the `ecommerce` mapping is undecided; adding it later is one map
 * entry. This is the only place the gate intentionally fails OPEN, and only because an unmapped
 * value means "no flag has claimed this route yet", not "the flag said no".
 *
 * Returns `undefined` when nothing gates the route. The return is deliberately widened to `string`
 * so the caller still validates against the known keys and catches a typo in page meta.
 */
export function resolveFeatureFlagForRoute(
    route: FeatureFlagRoute
): FeatureFlagKey | string | undefined {
    const byParam = route.meta.featureFlagByParam

    if (byParam) {
        const raw = route.params[byParam.param]
        const value = Array.isArray(raw) ? raw[0] : raw
        return value ? byParam.map[value] : undefined
    }

    return route.meta.featureFlag
}
